// CUR-644 — compliance registers (v1). Shared vocabulary for register fields
// and record payloads. The requirement source is the prototype annotation layer
// (github.com/Curium-Co/registers @ 5790fce); design decisions D1/D2 live in
// docs/plan/cur-644-registers-v1/plan.md.

/**
 * Field types a register field can take (REG-F8). Mirrors the FieldConfiguration
 * vocabulary where it overlaps, plus `yesno`, `email` and `user` which
 * FieldConfiguration lacks. `select` covers single and multi — multi-select is a
 * property of the field (`isMultiple`), not a separate type (REG-F5).
 */
export const REGISTER_FIELD_TYPES = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    NUMBER: 'number',
    CURRENCY: 'currency',
    DATE: 'date',
    SELECT: 'select',
    YESNO: 'yesno',
    EMAIL: 'email',
    USER: 'user',
} as const;

export type RegisterFieldType = (typeof REGISTER_FIELD_TYPES)[keyof typeof REGISTER_FIELD_TYPES];

/** Tuple form for `z.enum` input schemas (kept in sync by the `satisfies`). */
export const REGISTER_FIELD_TYPE_VALUES = [
    'text',
    'textarea',
    'number',
    'currency',
    'date',
    'select',
    'yesno',
    'email',
    'user',
] as const satisfies readonly RegisterFieldType[];

/**
 * Dropdown options are stored as their labels, in display order. Captured record
 * values store the option label as selected (never an option id), so renaming or
 * removing an option affects new entries only (REG-F6 by construction — plan D1).
 * Option labels are unique per field case-insensitively (REG-F7, app-enforced).
 */
export type RegisterFieldOptions = string[];

/**
 * The shell fields every register carries (REG-F9). Each register has exactly one
 * `RegisterField` row per system key; the row carries the per-register relabel
 * (e.g. title shown as "Asset name") and blocks label collisions between
 * custom fields and the shell (REG-F2). Shell fields cannot be archived (REG-A3).
 * Criticality is deliberately NOT shell (JLo, 2026-08-03): templates include an
 * ordinary select field where it earns its place.
 */
export const REGISTER_SYSTEM_FIELD_KEYS = ['TITLE', 'OWNER', 'NEXT_REVIEW'] as const;

export type RegisterSystemFieldKey = (typeof REGISTER_SYSTEM_FIELD_KEYS)[number];

/**
 * Non-field shell surfaces every register carries (REG-F9), in display order:
 * Reference leads, the rest trail the shell field labels in the builder's
 * locked-shell chips. Reserved as labels too — a custom field named
 * "Reference" would collide in display with the record ref column — and the
 * template test asserts no launch-template label collides with them.
 */
/**
 * The fixed trailing column of the snapshot download and viewer (REG-SN6, JLo
 * answer 1). Snapshot surfaces only — the live export stays fields-only.
 */
export const REGISTER_LINKED_ITEMS_COLUMN_HEADER = 'Linked items';

export const REGISTER_FIXED_SHELL_SURFACE_NAMES = [
    'Reference',
    'Documents',
    'Tasks',
    'Control checks',
    'Connected records',
] as const;

/**
 * Labels a custom field may not take. The displayed shell surfaces above, plus
 * `Ref` — the CSV header for the reference column (REG-I1). `Ref` is NOT a
 * displayed surface (it would render a bogus builder chip), but it must be
 * reserved: a custom field labelled "Ref" collides with the fixed column, so
 * export would emit the header twice and the custom value could never
 * round-trip, because import resolves "Ref" to the ignored reference column
 * before it consults field labels.
 */
export const REGISTER_RESERVED_FIELD_LABELS = [
    ...REGISTER_FIXED_SHELL_SURFACE_NAMES,
    'Ref',
    // Not a surface — a CSV-safety reservation. A field labelled `__proto__`
    // exports as that header, and papaparse's header mode cannot give the
    // parsed row an own `__proto__` string property, so the value can never be
    // read back. Reserving the label keeps the round trip total.
    '__proto__',
    // The fixed trailing column of the SNAPSHOT download and viewer (REG-SN6,
    // JLo answer 1). Same reasoning as `Ref`: a custom field with this label
    // would render two indistinguishable headers in the frozen table and two
    // identical CSV columns, in a file whose whole purpose is unambiguous
    // audit evidence.
    REGISTER_LINKED_ITEMS_COLUMN_HEADER,
] as const;

/**
 * The linked-items header to use for a given set of field labels.
 *
 * Reserving the label (above) stops NEW fields taking it, but cannot fix
 * payloads already frozen — snapshots are immutable — so a snapshot captured
 * from a register that had such a field would render two indistinguishable
 * columns. Disambiguate the fixed column rather than the user's data, and keep
 * suffixing until the name is genuinely free. Shared because the CSV download
 * and the viewer table must agree on the header.
 */
export function resolveLinkedItemsHeader(fieldLabels: readonly string[]): string {
    const taken = new Set(fieldLabels.map((label) => label.trim().toLowerCase()));
    if (!taken.has(REGISTER_LINKED_ITEMS_COLUMN_HEADER.toLowerCase())) return REGISTER_LINKED_ITEMS_COLUMN_HEADER;

    for (let suffix = 2; ; suffix += 1) {
        const candidate = `${REGISTER_LINKED_ITEMS_COLUMN_HEADER} (${suffix})`;
        if (!taken.has(candidate.toLowerCase())) return candidate;
    }
}

/**
 * A stored record value, written by the canonical value codec (plan D2):
 * - text/textarea/email → string
 * - number → JSON number
 * - currency → decimal string (precision-safe; never a JSON number)
 * - date → 'YYYY-MM-DD' string
 * - yesno → boolean
 * - select → option label string (string[] when the field is multi)
 * - user → OrgUser userId as string (string[] when multi); display names are
 *   resolved at render time and frozen into snapshots — never stored here
 */
export type RegisterRecordScalar = string | number | boolean | string[];

/**
 * A value flagged on import (REG-I3): the cell did not parse as the field's type,
 * so it is kept as text and marked. Flagged values are excluded from typed filter
 * predicates (a flagged string must not poison a numeric filter — plan D1).
 */
export type RegisterRecordFlaggedValue = {
    value: RegisterRecordScalar;
    flagged: true;
};

export function isRegisterRecordFlaggedValue(
    value: RegisterRecordScalar | RegisterRecordFlaggedValue | null,
): value is RegisterRecordFlaggedValue {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && value.flagged === true;
}

/**
 * The `RegisterRecord.data` JSON payload: custom-field values keyed by the
 * field's immutable storage key (`RegisterField.fieldId`, REG-F1). Healthy
 * values are bare scalars; only import-flagged values carry the envelope
 * (plan D1). Shell values (title/owner/next review) live in real columns,
 * never here.
 */
export type RegisterRecordData = Record<string, RegisterRecordScalar | RegisterRecordFlaggedValue | null>;

/**
 * Snapshot payloads (REG-SN*, plan D3), `RegisterSnapshot.schemaVersion = 1`.
 * A snapshot renders entirely from its own payload: field definitions, record
 * values and display strings (owner/user names, link titles) are frozen at
 * capture, so later renames, user removals or target deletions cannot change
 * what the auditor sees. Bigint ids are serialised as strings.
 */
export type RegisterSnapshotFieldEntry = {
    fieldId: string;
    label: string;
    type: RegisterFieldType;
    isRequired: boolean;
    isMultiple: boolean;
    options: RegisterFieldOptions | null;
    order: number;
    helpText: string | null;
    systemKey: RegisterSystemFieldKey | null;
    showAsColumn: boolean;
};

export type RegisterSnapshotFields = RegisterSnapshotFieldEntry[];

/** A linked entity frozen as displayed at capture (Q7 option b). */
export type RegisterSnapshotLinkEntry = {
    id: string;
    type: 'risk' | 'control' | 'obligation';
    title: string;
};

/** A user reference frozen as displayed at capture. */
export type RegisterSnapshotUserEntry = {
    id: string;
    name: string;
};

export type RegisterSnapshotRecordEntry = {
    id: string;
    ref: number;
    title: string;
    owner: RegisterSnapshotUserEntry | null;
    /** 'YYYY-MM-DD' */
    nextReview: string | null;
    data: RegisterRecordData;
    /** Display values for `user`-typed fields frozen at capture, keyed by fieldId. */
    userDisplays: Record<string, RegisterSnapshotUserEntry[]>;
    links: RegisterSnapshotLinkEntry[];
};

export type RegisterSnapshotRecords = RegisterSnapshotRecordEntry[];

/**
 * Entities a record can link to (REC-L7). Suppliers/MSP deliberately absent.
 * Order is the display order of the Connected records groups — empty groups
 * stay visible ("the gap is the finding").
 */
export const REGISTER_LINK_TARGET_TYPES = ['risk', 'control', 'obligation'] as const;

export type RegisterLinkTargetType = (typeof REGISTER_LINK_TARGET_TYPES)[number];

/** Structural caps (REG-N3). Confirmed defaults pending phase 0 answers. */
export const REGISTER_LIMITS = {
    /**
     * Max ACTIVE custom fields per register (REG-F3). Archived fields do not
     * consume the budget — archiving is never a delete (REG-A1), so counting
     * archived rows would let iteration permanently freeze a register.
     */
    MAX_FIELDS_PER_REGISTER: 50,
    /**
     * Hard bound on total rows including archived (payload + storage safety
     * valve behind the active cap; generous because archived rows only
     * accumulate through deliberate builder edits).
     */
    MAX_TOTAL_FIELD_ROWS_PER_REGISTER: 200,
    /** Max rows per CSV import (REG-I4). */
    MAX_IMPORT_ROWS: 5_000,
    /**
     * Byte ceiling for an uploaded import CSV (client pre-check, presign
     * `content-length-range` and the route-boundary HeadObject guard all
     * enforce the same number). Generous for 5,000 rows × 50 fields.
     */
    MAX_IMPORT_FILE_BYTES: 10 * 1024 * 1024,
    /**
     * Max records the live CSV export materialises in one response — a bound
     * for the read path, not a product limit (v1 registers are hundreds of
     * rows; imports add at most 5,000 per run). Past it the export refuses
     * with a visible error rather than truncating silently.
     */
    MAX_EXPORT_ROWS: 50_000,
    /**
     * Byte ceiling on the generated export CSV. The row cap alone does not
     * bound the response: one record can carry 50 fields including 10,000-char
     * textareas and whole over-cap flagged import values, so a legal
     * under-50,000-row export could still be hundreds of MB. The exporter
     * accumulates this budget incrementally and refuses the moment it is
     * exceeded, so neither the row buffer nor the CSV string can grow past it.
     *
     * Deliberately the SAME number as `MAX_IMPORT_FILE_BYTES`: the export is
     * the input side of the round trip (export → edit → re-import), so a CSV
     * bigger than the import ceiling is one the user could never bring back
     * anyway. It also keeps a single request's peak — the chunk array, the
     * joined string and its SuperJSON encoding — proportionate to v1 registers
     * (hundreds of rows) rather than to the largest file the caps allow.
     */
    MAX_EXPORT_BYTES: 10 * 1024 * 1024,
    /**
     * Hard ceiling on records considered per export query. The exporter also
     * projects away archived JSON values and applies a cumulative byte window
     * in PostgreSQL before decoding the page; this larger row ceiling therefore
     * avoids needless round trips without relaxing the transient payload bound.
     */
    EXPORT_PAGE_SIZE: 250,
    /**
     * Outer ceiling on a single imported cell. REG-N2 says an over-cap value
     * is FLAGGED, not truncated — but "flagged" cannot mean unbounded: without
     * this the only limit is the 10 MB file cap, so one pathological cell could
     * persist megabytes into `RegisterRecord.data` and, worse, push the whole
     * register past `MAX_EXPORT_BYTES` so nobody can export it again. Beyond
     * this bound the row is rejected instead, which the user can act on.
     * Deliberately far above `TEXTAREA_VALUE` so ordinary over-cap text still
     * flags rather than errors.
     */
    MAX_IMPORT_CELL_CHARS: 100_000,
    /**
     * Ceiling on DISTINCT person identifiers one import file may reference.
     * The row cap does not bound this: a file can hold up to 50 person columns
     * (each multi-valued), so a legal 10 MB CSV could otherwise expand into
     * ~a million distinct candidates — a huge `Set` and hundreds of sequential
     * lookups, on the queued path while the register row lock is held. Far
     * above any real org's membership, so only a pathological file trips it.
     */
    MAX_IMPORT_USER_CANDIDATES: 10_000,
    /**
     * Ceiling on parsed COLUMNS in an import file. The 10 MB object cap bounds
     * bytes but not column count — a header row of one-character names and
     * commas parses into millions of entries, each of which preview would
     * resolve, echo in `columns`/`unmappedHeaders`, and the modal would render
     * as a tag and a table column. Generously above the 50-field register cap
     * plus the shell and any stray spreadsheet columns.
     */
    MAX_IMPORT_COLUMNS: 500,
    /**
     * Ceiling on ONE column header. Applies to unmapped headers too: preview
     * echoes every header in `columns`/`unmappedHeaders` and repeats it as an
     * object key on each preview row, so a single oversized header amplifies a
     * bounded upload into a far larger response. Comfortably above any real
     * column name, and above the mapped-header bound (`FIELD_LABEL` + slack)
     * so an unmappable-but-sane header still reports as unmapped rather than
     * failing the file.
     */
    MAX_IMPORT_HEADER_LENGTH: 300,
    /** Proposed max options per select field (REG-N3 — confirm). */
    MAX_OPTIONS_PER_FIELD: 100,
    /** Max targets of one type in a single link batch (REC-L* picker). */
    MAX_LINKS_PER_BATCH: 100,
    /**
     * Max registers per org — the bound `listRegisters` relies on for its
     * deliberate lack of pagination (#1351 review follow-up).
     */
    MAX_REGISTERS_PER_ORG: 100,
    /**
     * Max snapshots per register — snapshots are immutable and undeletable
     * (REG-SN2), so their history grows monotonically; this cap is the bound
     * `listRegisterSnapshots` and the toolbar Select rely on for their
     * deliberate lack of pagination (same pattern as MAX_REGISTERS_PER_ORG).
     */
    MAX_SNAPSHOTS_PER_REGISTER: 100,
    /**
     * Max snapshot schedules per register (CUR-722). Schedules are deletable
     * config, so this is a sanity bound rather than a permanent budget — but it
     * keeps the schedules drawer unpaginated and, more importantly, stops a
     * register accumulating enough overlapping schedules to burn through
     * MAX_SNAPSHOTS_PER_REGISTER (which IS permanent) in a handful of cycles.
     */
    MAX_SNAPSHOT_SCHEDULES_PER_REGISTER: 10,
} as const;

/**
 * How often a snapshot schedule fires, with the labels shown in the UI.
 *
 * Mirrors the `RegisterSnapshotScheduleFrequency` Prisma enum; kept here so the
 * browser gets the labels without importing anything server-side. The recurring
 * values all step in whole months from the schedule's anchor day — picking
 * 31 August and "Quarterly" means 30 Nov, 28/29 Feb, 31 May, which is what
 * "every quarter end" has to mean.
 */
export const REGISTER_SNAPSHOT_SCHEDULE_FREQUENCY_LABELS = {
    ONE_OFF: 'Once',
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    ANNUALLY: 'Annually',
} as const satisfies Record<string, string>;

export type RegisterSnapshotScheduleFrequencyKey = keyof typeof REGISTER_SNAPSHOT_SCHEDULE_FREQUENCY_LABELS;

export const REGISTER_SNAPSHOT_SCHEDULE_FREQUENCIES = Object.keys(
    REGISTER_SNAPSHOT_SCHEDULE_FREQUENCY_LABELS,
) as readonly RegisterSnapshotScheduleFrequencyKey[];

/**
 * What a register accepts as an email, and the ONLY definition of it.
 *
 * Intentionally simple (bounded, no backtracking risk): one @ and no spaces.
 * Real validation is delivery, not a regex. Shared because the client control
 * has to apply the same grammar — a stricter client rule rejects values the
 * server has already stored, and `VInputWrapper` validates before its no-change
 * short-circuit, so the field cannot even be closed by clicking away.
 */
export const REGISTER_EMAIL_PATTERN = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

/** Length caps (REG-N2 — proposed defaults, confirm in phase 0). */
export const REGISTER_LENGTHS = {
    REGISTER_NAME: 120,
    SNAPSHOT_NAME: 120,
    /**
     * Shorter than SNAPSHOT_NAME because every capture appends " (dd/MM/yyyy)"
     * to it — 13 characters — and the result must still fit SNAPSHOT_NAME.
     */
    SNAPSHOT_SCHEDULE_NAME: 100,
    FIELD_LABEL: 100,
    OPTION_LABEL: 100,
    HELP_TEXT: 500,
    TEXT_VALUE: 500,
    TEXTAREA_VALUE: 10_000,
    EMAIL_VALUE: 254,
    DELETION_REASON: 2_000,
} as const;
